import styled from 'styled-components'
import PropTypes from 'prop-types'
import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useCombobox } from 'downshift'
import { matchSorter } from 'match-sorter'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import debounce from 'lodash/debounce'

import { SelectWrap, SelectMenuWrap, SelectMenu, SelectItem } from '../components'
import { focusedStyles, sizing } from '../styles'
import { itemToString } from '../utils'
import { TextInput } from '../text'
import { buttonReset, base } from '../../mixins'
import { PB40T, SLB10S } from '../../colors'

const getPadding = ({ size }) => {
  const [top, right, bottom, left] = sizing[size].clearable.padding.split(' ')

  switch (size) {
    case 'small':
      return `${top} calc(${right} + 20px) ${bottom} ${left}`
    case 'medium':
      return `${top} calc(${right} + 25px) ${bottom} ${left}`
    case 'large':
      return `${top} calc(${right} + 25px) ${bottom} ${left}`
  }
}
const getRightPosition = ({ size }) => {
  const right = sizing[size].icon.right

  switch (size) {
    case 'small':
      return `calc(${right} + 20px)`
    case 'medium':
      return `calc(${right} + 25px)`
    case 'large':
      return `calc(${right} + 25px)`
  }
}
const Combobox = styled.div`
  display: flex;
  align-items: center;
  position: relative;

  input {
    padding: ${getPadding};

    & + span {
      right: ${getRightPosition};
    }
  }

  &:hover,
  &:focus,
  &[aria-expanded='true'] {
    > button {
      display: block;
    }
  }

  &[data-menu-empty='true'] {
    > button {
      display: none;
    }
  }
`
const ToggleMenuButton = styled.button`
  ${buttonReset};
  ${base};
  display: none;
  padding: 0;
  outline: 0;
  line-height: initial;
  border: none;
  background: initial;
  position: absolute;
  right: ${({ size }) => (size === 'small' ? '14px' : '16px')};
  font-size: ${({ size }) => sizing[size].fontSize};
  cursor: pointer;
`
const ComboMenu = styled(SelectMenu)`
  margin-top: 10px;
`
const ComboMenuWrap = styled(SelectMenuWrap)`
  ${Combobox}[aria-expanded="false"] + &,
  ${Combobox}[data-menu-empty="true"] + & {
    display: none;
  }

  ${Combobox}[aria-expanded="true"] + & {
    ${ComboMenu} {
      ${focusedStyles};
      border-top: 1px solid ${SLB10S};
      border-radius: 5px;
    }
  }
`
const matchSorterOptions = {
  keys: [item => itemToString(item)],
  threshold: matchSorter.rankings.CONTAINS
}
const AutoSuggest = ({
  id,
  items,
  value,
  onBlur,
  onClear,
  onChange,
  preload,
  loadItems,
  labelledBy,
  renderItem,
  color = PB40T,
  width = 'auto',
  size = 'medium',
  placeholder = '',
  isDisabled = false,
  inputBoundByItems = false
}) => {
  const initialLoadedItems = useRef([])
  const [inputText, setInputText] = useState(itemToString(value))
  const [inputItems, setInputItems] = useState(items ?? [])
  const render = useMemo(() => {
    if (typeof renderItem === 'function') {
      return renderItem
    }

    return item => itemToString(item)
  }, [renderItem])
  const handleOnInputValueChange = useMemo(() => {
    if (typeof loadItems === 'function') {
      return debounce(
        async changes => {
          const newItems = await loadItems(changes.inputValue?.trim())

          setInputItems(newItems)
        },
        250,
        { leading: true }
      )
    }

    return changes => {
      setInputItems(matchSorter(items, changes.inputValue, matchSorterOptions))
    }
  }, [items, loadItems])
  const preloadItems = useMemo(() => {
    if (preload && typeof loadItems === 'function') {
      return async () => {
        initialLoadedItems.current = await loadItems(
          typeof preload === 'string' ? preload : ''
        )

        setInputItems(initialLoadedItems.current)
      }
    }

    return null
  }, [preload, loadItems])
  const handleOnClear = useCallback(async () => {
    setInputText('')

    if (initialLoadedItems.current.length) {
      setInputItems(initialLoadedItems.current)
    } else if (typeof loadItems === 'function') {
      setInputItems(await loadItems(''))
    } else {
      setInputItems(items)
    }

    if (typeof onClear === 'function') {
      onClear()
    }
  }, [onClear, items, loadItems])
  const {
    isOpen,
    openMenu,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps
  } = useCombobox({
    itemToString,
    items: inputItems,
    inputValue: inputText,
    initialSelectedItem: value,
    onSelectedItemChange: useCallback(
      changes => {
        setInputText(itemToString(changes.selectedItem))

        if (onChange) {
          const evt = new Event('change', {
            bubbles: true,
            cancelable: false
          })

          Object.defineProperty(evt, 'target', {
            writeable: false,
            value: { value: changes.selectedItem }
          })

          onChange(evt, changes.type)
        }
      },
      [onChange]
    ),
    onStateChange: useCallback(
      changes => {
        const { type } = changes

        switch (type) {
          case useCombobox.stateChangeTypes.InputChange: {
            if (typeof loadItems === 'function' || !inputBoundByItems) {
              setInputText(changes.inputValue)
              handleOnInputValueChange(changes)
            } else if (inputBoundByItems) {
              const nextItems = matchSorter(items, changes.inputValue, matchSorterOptions)

              if (nextItems.length) {
                setInputText(changes.inputValue)
                setInputItems(nextItems)
              }
            }

            break
          }
          case useCombobox.stateChangeTypes.ItemClick: {
            if (changes.inputValue && changes.inputValue !== inputText) {
              setInputText(changes.inputValue)
            }

            break
          }
        }
      },
      [loadItems, inputBoundByItems, items, inputText, handleOnInputValueChange]
    )
  })

  useEffect(() => {
    if (typeof preloadItems === 'function') {
      preloadItems()
    }
  }, [preloadItems])

  return (
    <SelectWrap width={width}>
      <Combobox
        {...getComboboxProps()}
        size={size}
        data-menu-empty={inputItems.length === 0}>
        <TextInput
          {...getInputProps({
            onBlur,
            onChange,
            value: inputText,
            onFocus: () => {
              if (!isOpen) {
                openMenu()
              }
            }
          })}
          id={id}
          size={size}
          color={color}
          onClear={handleOnClear}
          labelledBy={labelledBy}
          isDisabled={isDisabled}
          placeholder={placeholder}
        />
        <ToggleMenuButton
          {...getToggleButtonProps()}
          type="button"
          size={size}
          disabled={isDisabled}
          aria-label="toggle menu">
          <FontAwesomeIcon
            icon="chevron-down"
            color={PB40T}
            className={isOpen ? 'fa-rotate-180' : ''}
          />
        </ToggleMenuButton>
      </Combobox>
      <ComboMenuWrap>
        <ComboMenu {...getMenuProps()} data-select-menu="true">
          {isOpen &&
            inputItems.map((item, index) => (
              <SelectItem
                {...getItemProps({ item, index })}
                key={index}
                size={size}
                color={color}
                className={highlightedIndex === index ? 'highlighted' : ''}>
                {render(item)}
              </SelectItem>
            ))}
        </ComboMenu>
      </ComboMenuWrap>
    </SelectWrap>
  )
}

AutoSuggest.propTypes = {
  /** The items in the AutoSuggest as options */
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string
      })
    ])
  ),
  /** Callback to load items dynamically */
  loadItems: PropTypes.func,
  /** Whether to call `loadItems(preload)` to set the initial items, use `true` to call `loadItems('')`.  */
  preload: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /** The item that should be selected */
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string
    })
  ]),
  /** Whether the value entered in the input is restricted to characters matching values from the remaining available items. */
  inputBoundByItems: PropTypes.bool,
  /** Callback to apply custom rendering to the AutoSuggest items, `renderItem(item)` */
  renderItem: PropTypes.func,
  /** The callback when a user clicks on the clear text icon */
  onClear: PropTypes.func,
  /** The callback handler when the selected item changes */
  onChange: PropTypes.func,
  /** The callback handler when the select loses focus */
  onBlur: PropTypes.func,
  /** Whether the AutoSuggest is disabled */
  isDisabled: PropTypes.bool,
  /** The width of the AutoSuggest */
  width: PropTypes.string,
  /** The color of the items text */
  color: PropTypes.string,
  /** The relative size of the AutoSuggest when closed */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** The default text when no item is selected */
  placeholder: PropTypes.string,
  /** The id of the AutoSuggest, useful for accessibility */
  id: PropTypes.string,
  /** The id of the associated label (i.e. `Field` component), useful for accessibility */
  labelledBy: PropTypes.string
}

export { AutoSuggest }
